import { clear, element, forEachElement } from "../util/Utils";
import { SortingForm } from "../sorting/SortingForm";
import type { Appendable } from "../util/Appendable";
import type { FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import FilterSwitchBanner from "../filterBanner/FilterSwitchBanner";
import { events } from "../events";

const CLASS_REFINEMENT_BAR = "find_refinementBar";
const SELECTOR_REFINEMENT_BAR = `.${CLASS_REFINEMENT_BAR}`;

const ID_SORTING_FORM_ANCHOR = "heureka_desktopSorting";
const SELECTOR_SORTING_FORM_ANCHOR = `#${ID_SORTING_FORM_ANCHOR}`;
const ID_SORTING_FORM_PLACEHOLDER = "heureka_desktopSorting--placeholder";
const ID_FACET_VALUE_TAGS_ANCHOR = "find_facetValueTags";
const SELECTOR_FACET_VALUE_TAGS_ANCHOR = `#${ID_FACET_VALUE_TAGS_ANCHOR}`;
const ID_BANNER_ANCHOR = "heureka_filterBanner";
const SELECTOR_BANNER_ANCHOR = `#${ID_BANNER_ANCHOR}`;
const SELECTOR_FACET_VALUE_TAGS = `${SELECTOR_REFINEMENT_BAR} ${SELECTOR_FACET_VALUE_TAGS_ANCHOR}`;

export class RefinementBar {
  /*               */
  protected constructor(
    private readonly sortingFormAnchor: HTMLElement | null,
    private readonly facetValueTagsAnchor: HTMLElement | null,
    private readonly filterBannerAnchor: HTMLElement | null,
  ) {}

  static #create(elem: HTMLElement) {
    /*                                               */
    if (!document.getElementById(ID_BANNER_ANCHOR) && document.querySelector(SELECTOR_REFINEMENT_BAR)) {
      const ottoSwitchBannerAnchor = document.createElement("div");
      ottoSwitchBannerAnchor.id = ID_BANNER_ANCHOR;
      document.querySelector(SELECTOR_REFINEMENT_BAR)?.append(ottoSwitchBannerAnchor);
    }
    return new RefinementBar(
      element(SELECTOR_SORTING_FORM_ANCHOR, elem),
      element(SELECTOR_FACET_VALUE_TAGS_ANCHOR, elem),
      element(SELECTOR_BANNER_ANCHOR, elem),
    );
  }

  static facetValueTagsElement(rootElement?: ParentNode | null): HTMLElement | null {
    return element(SELECTOR_FACET_VALUE_TAGS, rootElement);
  }

  /*                  */

  static forEach(callback: (input: RefinementBar, index: number) => void, rootElement?: ParentNode) {
    forEachElement<HTMLElement>(
      SELECTOR_REFINEMENT_BAR,
      (elem, index) => callback(RefinementBar.#create(elem), index),
      rootElement,
    );
  }

  /*               */

  static lockAll() {
    RefinementBar.forEach((refinementBar) => refinementBar.lock());
  }

  static unlockAll() {
    RefinementBar.forEach((refinementBar) => refinementBar.unlock());
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    RefinementBar.forEach((refinementBar) => refinementBar.init(), rootElement);
    events.refinementBarLoaded();
  }

  init() {
    const form: SortingForm | undefined = SortingForm.template();
    if (form) {
      this.sortingForm = form;
    }
    this.addToFilterBanner(FilterSwitchBanner.fromTemplate()?.init());

    /*                                                                                                               */
    this.hideRefinementBarPlaceholder();
  }

  private hideRefinementBarPlaceholder() {
    const refinementBarPlaceholder = document.getElementById("heureka_refinementBar__placeholder");
    if (
      refinementBarPlaceholder &&
      !refinementBarPlaceholder.classList.contains("heureka_refinementBar__placeholder--hidden")
    ) {
      refinementBarPlaceholder.classList.add("heureka_refinementBar__placeholder--hidden");
    }
  }

  /*                       */

  public lock() {
    this.sortingFormAnchor?.classList.add("find_refinementBar--loading");
    this.facetValueTagsAnchor?.classList.add("find_refinementBar--loading");
    this.filterBannerAnchor?.classList.add("find_refinementBar--loading");
    return this;
  }

  public unlock() {
    this.sortingFormAnchor?.classList.remove("find_refinementBar--loading");
    this.facetValueTagsAnchor?.classList.remove("find_refinementBar--loading");
    this.filterBannerAnchor?.classList.remove("find_refinementBar--loading");
    return this;
  }

  set sortingForm(form: SortingForm) {
    const placeholder = document.getElementById(ID_SORTING_FORM_PLACEHOLDER);
    const anchor = placeholder ?? this.sortingFormAnchor;
    if (placeholder && this.sortingFormAnchor) {
      this.sortingFormAnchor.hidden = true;
    }
    if (anchor) {
      clear(anchor);
      anchor.appendChild(form.form);
    }
  }

  private addToFilterBanner(elem: Appendable | undefined) {
    const anchor = this.filterBannerAnchor;
    if (anchor && elem) {
      clear(anchor);
      elem.appendTo(anchor);
    }
  }
}
